import React, { useState, useEffect } from 'react';
import { 
  Mail, Send, Plus, X, Loader2, Paperclip, Truck, Store, FileText, Receipt,
  AlertCircle, User, Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified Send Email Modal for Order Communications
 * 
 * Supports 4 email types:
 * - invoice: Send invoice with PDF attachment
 * - quote: Send quote with PDF attachment  
 * - shipped: Order shipped notification
 * - pickup: Ready for pickup notification
 */

const EMAIL_TYPE_CONFIG = {
  invoice: {
    title: 'Send Invoice',
    icon: Receipt,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    buttonText: 'Send Invoice',
    successMessage: 'Invoice sent successfully!',
    supportsPdf: true,
    extraFields: [],
  },
  quote: {
    title: 'Send Quote',
    icon: FileText,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    buttonText: 'Send Quote',
    successMessage: 'Quote sent successfully!',
    supportsPdf: true,
    extraFields: [],
  },
  payment: {
    title: 'Send Payment Receipt',
    icon: Receipt,
    iconColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    buttonText: 'Send Receipt',
    successMessage: 'Payment receipt sent successfully!',
    supportsPdf: true,
    extraFields: [],
  },
  shipped: {
    title: 'Send Shipped Notification',
    icon: Truck,
    iconColor: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    buttonText: 'Send Notification',
    successMessage: 'Shipped notification sent!',
    supportsPdf: false,
    extraFields: ['tracking_number', 'carrier_name'],
  },
  pickup: {
    title: 'Send Pickup Notification',
    icon: Store,
    iconColor: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    buttonText: 'Send Notification',
    successMessage: 'Pickup notification sent!',
    supportsPdf: false,
    extraFields: ['pickup_instructions'],
  },
  statement: {
    title: 'Send Statement',
    icon: FileText,
    iconColor: 'text-[#8B7355]',
    bgColor: 'bg-[#8B7355]/5',
    borderColor: 'border-[#8B7355]/20',
    buttonText: 'Send Statement',
    successMessage: 'Statement sent successfully!',
    supportsPdf: true,
    extraFields: [],
  },
};

const SendEmailModal = ({
  open,
  onOpenChange,
  emailType, // 'invoice' | 'quote' | 'payment' | 'shipped' | 'pickup' | 'statement'
  order,
  payment, // Optional: payment object for 'payment' type
  customer, // Optional: customer object for 'statement' type
  statementData, // Optional: statement metadata for 'statement' type
  onSend, // async function that sends the email
  onSuccess,
}) => {
  const config = EMAIL_TYPE_CONFIG[emailType] || EMAIL_TYPE_CONFIG.invoice;
  const IconComponent = config.icon;
  
  // Recipient state
  const [toEmail, setToEmail] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [bccEmails, setBccEmails] = useState([]);
  const [newBccEmail, setNewBccEmail] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);
  
  // Editable message state
  const [messageText, setMessageText] = useState('');
  const [templateMessageText, setTemplateMessageText] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  
  // Extra fields for notifications
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [pickupInstructions, setPickupInstructions] = useState('');
  
  // UI state
  const [sending, setSending] = useState(false);
  const [showAddCc, setShowAddCc] = useState(false);
  const [showAddBcc, setShowAddBcc] = useState(false);
  
  // Initialize fields when modal opens or order/payment/customer changes
  useEffect(() => {
    if (open && (order || payment || customer)) {
      // Determine TO email based on email type
      let primaryEmail = '';
      let potentialCc = [];
      
      if (emailType === 'statement' && customer) {
        // For statements, use customer data
        primaryEmail = customer.email || '';
        // No auto CC for statements
      } else if (emailType === 'payment' && order) {
        // For payment receipts, use same logic as invoice/quote - from order's bill_to or customer
        const billToEmail = order.bill_to_snapshot?.email || 
                            order.invoice_snapshot?.bill_to_snapshot?.email;
        const customerEmail = order.customer_snapshot?.billing_email || 
                             order.customer_snapshot?.primary_email ||
                             order.customer_snapshot?.email;
        const orderEmail = order.customer_email;
        
        primaryEmail = billToEmail || customerEmail || orderEmail || '';
        
        // Auto-populate CC with order-level customer_email if different from TO
        if (orderEmail && orderEmail.toLowerCase() !== primaryEmail.toLowerCase()) {
          potentialCc.push(orderEmail);
        }
        // Also add customer primary email if different
        if (customerEmail && 
            customerEmail.toLowerCase() !== primaryEmail.toLowerCase() &&
            !potentialCc.some(e => e.toLowerCase() === customerEmail.toLowerCase())) {
          potentialCc.push(customerEmail);
        }
      } else if (order) {
        // For invoice/quote/shipped/pickup - use order data
        const billToEmail = order.bill_to_snapshot?.email || 
                            order.invoice_snapshot?.bill_to_snapshot?.email;
        const customerEmail = order.customer_snapshot?.billing_email || 
                             order.customer_snapshot?.primary_email ||
                             order.customer_snapshot?.email;
        const orderEmail = order.customer_email;
        
        primaryEmail = billToEmail || customerEmail || orderEmail || '';
        
        // Auto-populate CC with order-level customer_email if different from TO
        if (orderEmail && orderEmail.toLowerCase() !== primaryEmail.toLowerCase()) {
          potentialCc.push(orderEmail);
        }
        // Also add customer primary email if different
        if (customerEmail && 
            customerEmail.toLowerCase() !== primaryEmail.toLowerCase() &&
            !potentialCc.some(e => e.toLowerCase() === customerEmail.toLowerCase())) {
          potentialCc.push(customerEmail);
        }
      }
      
      setToEmail(primaryEmail);
      setCcEmails(potentialCc);
      
      // Reset BCC
      setBccEmails([]);
      setNewBccEmail('');
      setShowAddBcc(false);
      
      // Reset extra fields
      setTrackingNumber(order?.tracking_number || '');
      setCarrierName(order?.carrier_name || '');
      setPickupInstructions('');
      setAttachPdf(config.supportsPdf);
      setNewCcEmail('');
      setShowAddCc(false);
    }
  }, [open, order, payment, customer, emailType, config.supportsPdf]);

  // Fetch rendered template message when modal opens
  useEffect(() => {
    if (!open) return;
    // Map emailType to template_type
    const typeMap = {
      invoice: 'invoice_send',
      quote: 'quote_send',
      statement: 'statement_send',
      shipped: 'order_shipped',
      pickup: 'ready_for_pickup',
      payment: 'payment_receipt',
    };
    const templateType = typeMap[emailType];
    if (!templateType) {
      setMessageText('');
      setTemplateMessageText('');
      return;
    }

    const fetchTemplate = async () => {
      setMessageLoading(true);
      try {
        // Build variables from order/customer data
        const variables = {};
        if (order) {
          variables.customer_name = order?.customer_snapshot?.name || order?.bill_to_snapshot?.name || '';
          variables.invoice_number = order?.invoice_number || order?.order_id || '';
          variables.order_number = order?.order_id || '';
          variables.invoice_date = order?.invoice?.issued_at ? new Date(order.invoice.issued_at).toLocaleDateString() : '';
          variables.due_date = order?.invoice?.due_at ? new Date(order.invoice.due_at).toLocaleDateString() : '';
          variables.amount_due = order?.invoice?.balance_due_cents ? `$${(order.invoice.balance_due_cents / 100).toFixed(2)}` : '';
          variables.total_amount = order?.invoice?.total ? `$${order.invoice.total.toFixed(2)}` : '';
        }
        if (customer) {
          variables.customer_name = customer.name || '';
          variables.customer_email = customer.email || '';
        }
        if (statementData) {
          variables.period_from = statementData.periodFrom || '';
          variables.period_to = statementData.periodTo || '';
        }

        const res = await fetch(`${API_URL}/api/email-templates/${templateType}/render`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variables }),
        });
        if (res.ok) {
          const data = await res.json();
          setMessageText(data.rendered_plain_text || '');
          setTemplateMessageText(data.rendered_plain_text || '');
        }
      } catch {
        // Silently fail — message area will just be empty
      } finally {
        setMessageLoading(false);
      }
    };

    fetchTemplate();
  }, [open, emailType, order, customer, statementData]);

  // Add CC email
  const handleAddCc = () => {
    if (!newCcEmail) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCcEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Check for duplicates
    const normalized = newCcEmail.toLowerCase();
    if (normalized === toEmail.toLowerCase()) {
      toast.error('CC email cannot be the same as TO email');
      return;
    }
    if (ccEmails.some(e => e.toLowerCase() === normalized)) {
      toast.error('This email is already in the CC list');
      return;
    }
    
    setCcEmails([...ccEmails, newCcEmail]);
    setNewCcEmail('');
    setShowAddCc(false);
  };
  
  // Remove CC email
  const handleRemoveCc = (emailToRemove) => {
    setCcEmails(ccEmails.filter(e => e !== emailToRemove));
  };
  
  // Add BCC email
  const handleAddBcc = () => {
    if (!newBccEmail) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newBccEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Check for duplicates
    const normalized = newBccEmail.toLowerCase();
    if (normalized === toEmail.toLowerCase()) {
      toast.error('BCC email cannot be the same as TO email');
      return;
    }
    if (bccEmails.some(e => e.toLowerCase() === normalized)) {
      toast.error('This email is already in the BCC list');
      return;
    }
    
    setBccEmails([...bccEmails, newBccEmail]);
    setNewBccEmail('');
    setShowAddBcc(false);
  };
  
  // Remove BCC email
  const handleRemoveBcc = (emailToRemove) => {
    setBccEmails(bccEmails.filter(e => e !== emailToRemove));
  };
  
  // Send email
  const handleSend = async () => {
    if (!toEmail) {
      toast.error('Recipient email is required');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(toEmail)) {
      toast.error('Please enter a valid recipient email');
      return;
    }
    
    setSending(true);
    try {
      // Build request data based on email type
      const requestData = {
        to_email: toEmail,
        cc_emails: ccEmails.filter(e => e && e.toLowerCase() !== toEmail.toLowerCase()),
        bcc_emails: bccEmails.filter(e => e && e.toLowerCase() !== toEmail.toLowerCase()),
      };
      
      // Add type-specific fields
      if (emailType === 'invoice' || emailType === 'quote' || emailType === 'statement') {
        requestData.attach_pdf = attachPdf;
      }
      if (emailType === 'shipped') {
        requestData.tracking_number = trackingNumber;
        requestData.carrier_name = carrierName;
      }
      if (emailType === 'pickup') {
        requestData.pickup_instructions = pickupInstructions;
      }

      // Add custom message if edited
      if (messageText && messageText !== templateMessageText) {
        // Convert plain text to simple HTML
        const htmlBody = messageText
          .split('\n\n')
          .map(para => `<p>${para.replace(/\n/g, '<br/>')}</p>`)
          .join('\n');
        requestData.custom_body_html = htmlBody;
      }
      
      await onSend(requestData);
      
      toast.success(config.successMessage);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };
  
  // Get source label for an email
  const getEmailSource = (email) => {
    if (!email) return null;
    
    // Statement: use customer data
    if (emailType === 'statement' && customer) {
      if (email.toLowerCase() === customer.email?.toLowerCase()) {
        return { label: 'Account Email', icon: Building2 };
      }
      return { label: 'Added', icon: Mail };
    }
    
    if (!order) return null;
    
    const billToEmail = order.bill_to_snapshot?.email || 
                        order.invoice_snapshot?.bill_to_snapshot?.email;
    const customerBillingEmail = order.customer_snapshot?.billing_email;
    const customerPrimaryEmail = order.customer_snapshot?.primary_email || order.customer_snapshot?.email;
    const orderEmail = order.customer_email;
    
    if (email.toLowerCase() === billToEmail?.toLowerCase()) {
      return { label: 'Billing Contact', icon: Building2 };
    }
    if (email.toLowerCase() === customerBillingEmail?.toLowerCase()) {
      return { label: 'Account Billing', icon: Building2 };
    }
    if (email.toLowerCase() === customerPrimaryEmail?.toLowerCase()) {
      return { label: 'Primary Contact', icon: User };
    }
    if (email.toLowerCase() === orderEmail?.toLowerCase()) {
      return { label: 'Order Contact', icon: Mail };
    }
    return { label: 'Added', icon: Mail };
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`p-1.5 rounded ${config.bgColor}`}>
              <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
            </div>
            {config.title}
          </DialogTitle>
          <DialogDescription>
            {order?.invoice_number && emailType === 'invoice' && (
              <span>Invoice #{order.invoice_number}</span>
            )}
            {order?.quote_number && emailType === 'quote' && (
              <span>Quote #{order.quote_number}</span>
            )}
            {emailType === 'payment' && payment && (
              <span>Payment #{payment.payment_number || payment.id} • ${((payment.amount_cents || 0) / 100).toFixed(2)}</span>
            )}
            {(emailType === 'shipped' || emailType === 'pickup') && (
              <span>Order #{order?.order_id}</span>
            )}
            {emailType === 'statement' && statementData && (
              <span>Statement for {statementData.customerName} | {statementData.periodFrom} to {statementData.periodTo}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* TO Field */}
          <div className="space-y-2">
            <Label htmlFor="to-email" className="text-sm font-medium">
              To <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="to-email"
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="pr-24"
                data-testid="send-email-to-input"
              />
              {toEmail && getEmailSource(toEmail) && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    {React.createElement(getEmailSource(toEmail).icon, { className: "w-3 h-3" })}
                    {getEmailSource(toEmail).label}
                  </Badge>
                </div>
              )}
            </div>
          </div>
          
          {/* CC Field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">CC</Label>
              {!showAddCc && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddCc(true)}
                  className="h-7 text-xs"
                  data-testid="add-cc-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add CC
                </Button>
              )}
            </div>
            
            {/* Existing CC emails */}
            {ccEmails.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {ccEmails.map((email, idx) => {
                  const source = getEmailSource(email);
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${config.bgColor} ${config.borderColor} border`}
                    >
                      {source && React.createElement(source.icon, { className: "w-3 h-3 opacity-60" })}
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCc(email)}
                        className="ml-1 hover:text-red-600 transition-colors"
                        data-testid={`remove-cc-${idx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Add new CC input */}
            {showAddCc && (
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  placeholder="cc@example.com"
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCc();
                    }
                  }}
                  data-testid="new-cc-input"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddCc}
                  data-testid="confirm-add-cc-btn"
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddCc(false);
                    setNewCcEmail('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {ccEmails.length === 0 && !showAddCc && (
              <p className="text-xs text-gray-500">No CC recipients</p>
            )}
          </div>
          
          {/* BCC Field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">BCC</Label>
              {!showAddBcc && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddBcc(true)}
                  className="h-7 text-xs"
                  data-testid="add-bcc-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add BCC
                </Button>
              )}
            </div>
            
            {/* Existing BCC emails */}
            {bccEmails.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {bccEmails.map((email, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-gray-100 border border-gray-200"
                  >
                    <span>{email}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveBcc(email)}
                      className="ml-1 hover:text-red-600 transition-colors"
                      data-testid={`remove-bcc-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add new BCC input */}
            {showAddBcc && (
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newBccEmail}
                  onChange={(e) => setNewBccEmail(e.target.value)}
                  placeholder="bcc@example.com"
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBcc();
                    }
                  }}
                  data-testid="new-bcc-input"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddBcc}
                  data-testid="confirm-add-bcc-btn"
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddBcc(false);
                    setNewBccEmail('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {bccEmails.length === 0 && !showAddBcc && (
              <p className="text-xs text-gray-500">No BCC recipients</p>
            )}
          </div>
          
          {/* Editable Message Area */}
          {['invoice', 'quote', 'statement', 'shipped', 'pickup', 'payment'].includes(emailType) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Message</label>
                {messageText !== templateMessageText && (
                  <button
                    onClick={() => setMessageText(templateMessageText)}
                    className="text-xs text-[#8B7355] hover:text-[#6B5B4A] font-medium transition-colors"
                    data-testid="reset-to-template-btn"
                  >
                    Reset to Template
                  </button>
                )}
              </div>
              {messageLoading ? (
                <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading template...
                </div>
              ) : (
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono leading-relaxed focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355] resize-y"
                  placeholder="Enter your message..."
                  data-testid="message-textarea"
                />
              )}
              {emailType === 'statement' && (
                <p className="text-xs text-gray-400">Invoice tables, payments, and aging summary will be auto-appended below your message.</p>
              )}
            </div>
          )}
          
          {/* PDF Attachment Option (for Invoice/Quote/Payment) */}
          {config.supportsPdf && (
            <div className={`flex items-center justify-between p-3 rounded-lg ${config.bgColor} border ${config.borderColor}`}>
              <div className="flex items-center gap-2">
                <Paperclip className={`w-4 h-4 ${config.iconColor}`} />
                <div>
                  <p className="text-sm font-medium">Attach PDF</p>
                  <p className="text-xs text-gray-500">
                    {emailType === 'invoice' ? 'Include invoice PDF' : 
                     emailType === 'quote' ? 'Include quote PDF' : 
                     emailType === 'payment' ? 'Include payment receipt PDF' : 'Include PDF'}
                  </p>
                </div>
              </div>
              <Checkbox
                id="attach-pdf"
                checked={attachPdf}
                onCheckedChange={setAttachPdf}
                data-testid="attach-pdf-checkbox"
              />
            </div>
          )}
          
          {/* Extra Fields for Shipped Notification */}
          {emailType === 'shipped' && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Shipping Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="tracking-number" className="text-xs">Tracking Number</Label>
                  <Input
                    id="tracking-number"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="e.g., 1Z999AA10123456784"
                    className="text-sm"
                    data-testid="tracking-number-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="carrier-name" className="text-xs">Carrier</Label>
                  <Input
                    id="carrier-name"
                    value={carrierName}
                    onChange={(e) => setCarrierName(e.target.value)}
                    placeholder="e.g., UPS, FedEx"
                    className="text-sm"
                    data-testid="carrier-name-input"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Extra Fields for Pickup Notification */}
          {emailType === 'pickup' && (
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
              <Label htmlFor="pickup-instructions" className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Pickup Instructions (Optional)
              </Label>
              <Textarea
                id="pickup-instructions"
                value={pickupInstructions}
                onChange={(e) => setPickupInstructions(e.target.value)}
                placeholder="Any special instructions for customer pickup..."
                rows={2}
                className="text-sm"
                data-testid="pickup-instructions-input"
              />
            </div>
          )}
          
          {/* Warning if no TO email */}
          {!toEmail && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-xs">No recipient email found. Please enter an email address.</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !toEmail}
            data-testid="confirm-send-email-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {config.buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendEmailModal;
